'use client';

import { useCallback } from 'react';

import { useTrackImportedAsset } from '~/hooks/events/useTrackImportedAsset';
import { usePremiereMessaging } from '~/hooks/usePremiereMessaging';
import { usePremiereToasts } from '~/hooks/usePremiereToasts';
import { useImportProgressStore } from '~/stores/useImportProgressStore';
import { recordClipImport } from '~/utils/clipImportRecord';
import { getSingleImportSuccessToastMessage } from '~/utils/getSingleImportSuccessToastMessage';
import { importSuccessToastOptions } from '~/utils/importSuccessToastOptions';
import { resolveImportBatchComplete } from '~/utils/waitForImportBatchComplete';
import { resolveImportComplete } from '~/utils/waitForImportComplete';

/**
 * Subscribes to host ImportProgress / ImportComplete and updates import store,
 * analytics, lane mutex, and per-clip toasts for single-file imports.
 *
 * Mount once at workspace layout level (see {@link PremiereImportLifecycle}).
 */
export const usePremiereImportLifecycle = () => {
  const updateProgress = useImportProgressStore((state) => state.updateProgress);
  const completeImport = useImportProgressStore((state) => state.completeImport);
  const failImport = useImportProgressStore((state) => state.failImport);
  const { showErrorToast, showToast } = usePremiereToasts();
  const { trackImportedAsset } = useTrackImportedAsset();

  const handleImportProgress = useCallback(
    (data: { bytes: number; fileName: string }) => {
      const state = useImportProgressStore.getState();
      const isKnownImport = data.fileName in state.imports;

      if (state.importLaneOwner == null && state.batch == null && !isKnownImport) {
        return;
      }

      updateProgress(data.fileName, data.bytes);
    },
    [updateProgress],
  );

  const handleImportComplete = useCallback(
    (data: { success: boolean; error?: string; fileName?: string }) => {
      if (data.fileName) {
        resolveImportComplete(data.fileName, { error: data.error, success: data.success });
      }

      const state = useImportProgressStore.getState();
      const importMeta = data.fileName ? state.imports[data.fileName] : undefined;
      const isActiveSingleImport =
        state.importLaneOwner === 'single' &&
        state.singleLaneClipId != null &&
        (importMeta?.clipId === state.singleLaneClipId ||
          (data.fileName != null && data.fileName === state.singleLaneFileName));

      if (data.success && data.fileName && importMeta?.clipId && importMeta?.assetId) {
        trackImportedAsset({
          asset_id: importMeta.assetId,
          clip_id: importMeta.clipId,
          file_name: data.fileName,
        });

        recordClipImport({
          altResId: importMeta.altResId,
          assetId: importMeta.assetId,
          clipId: importMeta.clipId,
          fileName: data.fileName,
          kind: importMeta.kind ?? 'original',
        });
      }

      if (data.success) {
        if (data.fileName) {
          completeImport(data.fileName);
        }
      } else {
        failImport(data.fileName);
      }

      // Per-clip toasts only for the active single-file import. Batch shows one summary
      // from `useBatchImportClips`; late ImportComplete events after a batch finishes
      // have no lane owner and are ignored here.
      if (!isActiveSingleImport) {
        return;
      }

      if (data.success) {
        showToast(getSingleImportSuccessToastMessage(data.fileName), importSuccessToastOptions());
      } else {
        const name = data.fileName ?? 'file';

        showErrorToast(data.error ?? `Failed to import ${name} — unsupported format`);
      }

      state.releaseImportLane();
    },
    [completeImport, failImport, showErrorToast, showToast, trackImportedAsset],
  );

  const handleImportBatchComplete = useCallback(
    (data: {
      batchId: string;
      downloadFailures: Array<{ error: string; fileName: string }>;
      importError?: string;
      importedFileNames: string[];
    }) => {
      const state = useImportProgressStore.getState();

      if (!state.activeHostBatchIds.includes(data.batchId)) {
        if (state.abandonedHostBatchIds.includes(data.batchId)) {
          useImportProgressStore.getState().finishHostBatch(data.batchId);
        }

        return;
      }

      resolveImportBatchComplete(data.batchId, data);

      const isAbandoned = state.abandonedHostBatchIds.includes(data.batchId);

      if (!isAbandoned) {
        for (const fileName of data.importedFileNames) {
          const importMeta = useImportProgressStore.getState().imports[fileName];

          if (importMeta?.clipId && importMeta?.assetId) {
            trackImportedAsset({
              asset_id: importMeta.assetId,
              clip_id: importMeta.clipId,
              file_name: fileName,
            });
          }

          completeImport(fileName);
        }

        for (const failure of data.downloadFailures) {
          failImport(failure.fileName);
        }

        if (data.importError) {
          const importedSet = new Set(data.importedFileNames);
          const failedDownloadNames = new Set(data.downloadFailures.map((failure) => failure.fileName));
          const remainingImports = useImportProgressStore.getState().imports;

          for (const fileName of Object.keys(remainingImports)) {
            if (importedSet.has(fileName) || failedDownloadNames.has(fileName)) {
              continue;
            }

            failImport(fileName);
          }
        }

        useImportProgressStore.setState({ pendingImportQueue: [] });
      }

      state.finishHostBatch(data.batchId);
    },
    [completeImport, failImport, trackImportedAsset],
  );

  usePremiereMessaging({
    onImportBatchComplete: handleImportBatchComplete,
    onImportComplete: handleImportComplete,
    onImportProgress: handleImportProgress,
  });
};
